/**
 * **A small drawing of a header or a footer layout** — C24 items 2 and 13,
 * [ADR-0046](../../../../../docs/adr/0046-more-liberty-in-the-look-layouts-pairs-and-a-footer-of-slots.md) §1, §4.
 *
 * The owner, 2026-10-01:
 *
 * > *at Navigation, show header and footer style as shown in Header & footer
 * > layout, How your site looks. same sectin at both places to improve ux*
 *
 * So this lives here rather than inside *How your site looks*, and **both**
 * screens draw it: the look page offers the twelve as a choice, Navigation
 * shows the two the firm has chosen with a link to change them. One component,
 * so the two screens cannot come to disagree about what `split` looks like —
 * which is the whole of item 13.
 *
 * **It is a drawing and not a preview.** Each is a few rectangles in the app's
 * own theme tokens: enough to tell seven headers apart at a glance, and not a
 * promise about any particular template's typography. The real thing is the
 * preview link.
 *
 * **Twelve since C24**, from five: `split · centred · simple · stacked ·
 * inline · bar · minimal` and `compact · columns · wide · centred · stacked`.
 * `centred` is a header layout *and* a footer layout with two different
 * drawings, so the diagram takes the surface as well as the key.
 */

import type { ReactNode } from 'react'

export type LayoutSurface = 'header' | 'footer'

/** The layouts, in the firm's words. An unknown value gets its own name. */
export const LAYOUT_WORDS: Record<LayoutSurface, Record<string, { label: string; note: string }>> = {
  header: {
    split: { label: 'Split', note: 'Name left, menu right' },
    centred: { label: 'Centred', note: 'Name and menu in the middle' },
    simple: { label: 'Simple', note: 'Name and menu together on the left' },
    stacked: { label: 'Stacked', note: 'Your name above a full-width menu' },
    inline: { label: 'Inline', note: 'Name, menu and contact on one line' },
    bar: { label: 'Contact bar', note: 'A thin contact strip above the menu' },
    minimal: { label: 'Minimal', note: 'Just your name and a small menu' },
  },
  footer: {
    compact: { label: 'Compact', note: 'One line at the bottom' },
    columns: { label: 'Columns', note: 'Your links in columns beside your details' },
    wide: { label: 'Wide', note: 'Columns across the page, details beneath' },
    centred: { label: 'Centred', note: 'Everything centred, one column' },
    rows: { label: 'Rows', note: 'Each group on its own row' },
  },
}

/** A key's words, or the key itself made readable. */
export function layoutWords(
  surface: LayoutSurface,
  key: string,
): { label: string; note: string } {
  return (
    LAYOUT_WORDS[surface][key] ?? {
      label: key.charAt(0).toUpperCase() + key.slice(1).replace(/[-_]/g, ' '),
      note: '',
    }
  )
}

const NAME = 'fill-primary'
const ITEM = 'fill-muted-foreground/50'
const RULE = 'fill-border'

function headerParts(layout: string): ReactNode {
  switch (layout) {
    case 'split':
      return (
        <>
          <rect x="12" y="16" width="40" height="8" rx="2" className={NAME} />
          {[76, 94, 112, 130].map((x) => (
            <rect key={x} x={x} y="18" width="14" height="4" rx="2" className={ITEM} />
          ))}
          <rect x="0" y="38" width="160" height="1" className={RULE} />
        </>
      )
    case 'centred':
      return (
        <>
          <rect x="57" y="10" width="46" height="8" rx="2" className={NAME} />
          {[36, 58, 80, 102].map((x) => (
            <rect key={x} x={x} y="26" width="16" height="4" rx="2" className={ITEM} />
          ))}
          <rect x="0" y="38" width="160" height="1" className={RULE} />
        </>
      )
    case 'simple':
      return (
        <>
          <rect x="12" y="16" width="38" height="8" rx="2" className={NAME} />
          {[58, 78, 98, 118].map((x) => (
            <rect key={x} x={x} y="18" width="16" height="4" rx="2" className={ITEM} />
          ))}
          <rect x="0" y="38" width="160" height="1" className={RULE} />
        </>
      )
    case 'stacked':
      return (
        <>
          <rect x="12" y="8" width="46" height="8" rx="2" className={NAME} />
          <rect x="0" y="22" width="160" height="1" className={RULE} />
          {[12, 40, 68, 96].map((x) => (
            <rect key={x} x={x} y="28" width="20" height="4" rx="2" className={ITEM} />
          ))}
          <rect x="0" y="38" width="160" height="1" className={RULE} />
        </>
      )
    case 'inline':
      return (
        <>
          <rect x="12" y="17" width="30" height="7" rx="2" className={NAME} />
          {[50, 70, 90].map((x) => (
            <rect key={x} x={x} y="18" width="15" height="4" rx="2" className={ITEM} />
          ))}
          {/* The contact detail that rides the same line. */}
          <rect x="118" y="18" width="30" height="4" rx="2" className={ITEM} />
          <rect x="0" y="38" width="160" height="1" className={RULE} />
        </>
      )
    case 'bar':
      return (
        <>
          {/* The strip, and the rule under it. */}
          <rect x="104" y="5" width="20" height="3" rx="1.5" className={ITEM} />
          <rect x="128" y="5" width="20" height="3" rx="1.5" className={ITEM} />
          <rect x="0" y="13" width="160" height="1" className={RULE} />
          <rect x="12" y="21" width="38" height="8" rx="2" className={NAME} />
          {[86, 106, 126].map((x) => (
            <rect key={x} x={x} y="23" width="16" height="4" rx="2" className={ITEM} />
          ))}
          <rect x="0" y="38" width="160" height="1" className={RULE} />
        </>
      )
    case 'minimal':
      return (
        <>
          <rect x="12" y="16" width="36" height="8" rx="2" className={NAME} />
          {/* One small menu, and nothing else. */}
          <rect x="128" y="17" width="20" height="6" rx="2" className={ITEM} />
          <rect x="0" y="38" width="160" height="1" className={RULE} />
        </>
      )
    default:
      return <rect x="0" y="38" width="160" height="1" className={RULE} />
  }
}

function footerParts(layout: string): ReactNode {
  switch (layout) {
    case 'compact':
      return (
        <>
          <rect x="0" y="12" width="160" height="1" className={RULE} />
          <rect x="12" y="24" width="34" height="5" rx="2" className={NAME} />
          {[96, 114, 132].map((x) => (
            <rect key={x} x={x} y="25" width="14" height="3" rx="1.5" className={ITEM} />
          ))}
        </>
      )
    case 'columns':
      return (
        <>
          <rect x="0" y="6" width="160" height="1" className={RULE} />
          {[12, 52, 92, 132].map((x, i) => (
            <g key={x}>
              <rect x={x} y="13" width={i === 0 ? 28 : 18} height="4" rx="2" className={i === 0 ? NAME : ITEM} />
              <rect x={x} y="21" width="16" height="3" rx="1.5" className={ITEM} />
              <rect x={x} y="27" width="12" height="3" rx="1.5" className={ITEM} />
            </g>
          ))}
        </>
      )
    case 'wide':
      return (
        <>
          <rect x="0" y="4" width="160" height="1" className={RULE} />
          {[8, 46, 84, 122].map((x) => (
            <g key={x}>
              <rect x={x} y="10" width="26" height="4" rx="2" className={ITEM} />
              <rect x={x} y="17" width="20" height="3" rx="1.5" className={ITEM} />
            </g>
          ))}
          <rect x="0" y="26" width="160" height="1" className={RULE} />
          <rect x="8" y="31" width="34" height="4" rx="2" className={NAME} />
          <rect x="112" y="32" width="40" height="3" rx="1.5" className={ITEM} />
        </>
      )
    case 'centred':
      return (
        <>
          <rect x="0" y="6" width="160" height="1" className={RULE} />
          <rect x="62" y="12" width="36" height="5" rx="2" className={NAME} />
          <rect x="52" y="21" width="56" height="3" rx="1.5" className={ITEM} />
          {[58, 76, 94].map((x) => (
            <rect key={x} x={x} y="29" width="14" height="3" rx="1.5" className={ITEM} />
          ))}
        </>
      )
    case 'rows':
      return (
        <>
          <rect x="0" y="4" width="160" height="1" className={RULE} />
          <rect x="12" y="9" width="36" height="5" rx="2" className={NAME} />
          <rect x="12" y="19" width="60" height="3" rx="1.5" className={ITEM} />
          <rect x="12" y="26" width="46" height="3" rx="1.5" className={ITEM} />
          <rect x="12" y="33" width="52" height="3" rx="1.5" className={ITEM} />
        </>
      )
    default:
      return <rect x="0" y="20" width="160" height="1" className={RULE} />
  }
}

export function LayoutDiagram({
  layout,
  surface,
  className,
}: {
  layout: string
  /** Which of the two the key belongs to. `centred` is both. */
  surface: LayoutSurface
  className?: string
}) {
  return (
    <svg
      viewBox="0 0 160 40"
      role="img"
      aria-label={`${layoutWords(surface, layout).label} ${surface} layout`}
      data-layout-diagram={layout}
      data-layout-surface={surface}
      className={
        className ?? 'block h-auto w-full rounded-md border border-border bg-background'
      }
    >
      {surface === 'header' ? headerParts(layout) : footerParts(layout)}
    </svg>
  )
}
